import { GRID_DIRECTION } from "../const/grid-directions.const";
import { ENDPOINT_RADIUS } from "../components/cell/grid-cell.config";
import { pointInCircle } from "../components/cell/grid-cell.utils";
import { GridConfig } from "./GridConfig";

export class ConnectingLineHandlerUtils {
    grid;

    gridConfig;

    instanceId;

    constructor(instanceId) {
        this.instanceId = instanceId;
        this.gridConfig = new GridConfig(this.instanceId);
    }

    /**
     * @description Gets the possible endpoitns of the provided cell
     * @param {*} cell
     */
    getEndpointsPositions(cell) {
        const container = cell.ref.getContainerInstance();
        const returnObj = {}; const
            { layout } = container;
        returnObj[GRID_DIRECTION.LEFT] = { x: layout.x, y: layout.y + (layout.height / 2) };
        returnObj[GRID_DIRECTION.RIGHT] = { x: layout.x + layout.width, y: layout.y + (layout.height / 2) };
        returnObj[GRID_DIRECTION.BOTTOM] = { x: layout.x + (layout.width / 2), y: layout.y + layout.height };
        returnObj[GRID_DIRECTION.TOP] = { x: layout.x + (layout.width / 2), y: layout.y };
        return returnObj;
    }

    /**
     * @description Filters the endpoint position to only redraw those that have a incomign or outgoing connection
     */
    filterEndpointPositions(cell, connectorsMap, endpointPositions) {
        const pointersData = {};
        const endpointsCounter = this.getInitialEndpointsCounter();
        const outgoing = this.getConnectorsFromMap(cell.outgoing, connectorsMap);
        const incoming = this.getConnectorsFromMap(cell.incoming, connectorsMap);
        const posKeys = Object.keys(endpointPositions);
        for (let i = 0; i < posKeys.length; i++) {
            const pos = endpointPositions[posKeys[i]];
            const circle = { x: pos.x, y: pos.y, radius: ENDPOINT_RADIUS * 2 };
            for (let x = 0; x < incoming.length; x++) {
                const { instance } = incoming[x];
                const { path } = instance.layout;
                const incomingEndPoint = path[path.length - 1];
                const insideEndpoint = pointInCircle(incomingEndPoint.x, incomingEndPoint.y, circle.x, circle.y, circle.radius);
                if (insideEndpoint) pointersData[posKeys[i]] = { uuid: instance.uuid, value: 1 };
            }
            for (let x = 0; x < outgoing.length; x++) {
                const { instance } = outgoing[x];
                const { path } = instance.layout;
                const incomingEndPoint = path[0];
                const insideEndpoint = pointInCircle(incomingEndPoint.x, incomingEndPoint.y, circle.x, circle.y, circle.radius);
                if (insideEndpoint) pointersData[posKeys[i]] = { uuid: instance.uuid, value: 1 };
            }
        }

        return pointersData;
    }

    /**
     * @description changes a {x,y} object into a array [x, y]
     * @param {*} obj
     */
    turnObjectPathIntoMatrixPath(obj) {
        const toReturn = [];
        for (let i = 0; i < obj.length; i++) {
            toReturn.push([obj[i].x, obj[i].y]);
        }
        return toReturn;
    }

    /**
     * @description changes a array [x, y] into a  {x,y} object
     * @param {*} matrix
     */
    turnMatrixIntoObjectPath(matrix) {
        const toReturn = [];
        for (let i = 0; i < matrix.length; i++) {
            toReturn.push([]);
            for (let line = 0; line < matrix[i].length; line++) {
                toReturn[i].push({ x: matrix[i][line][0], y: matrix[i][line][1] });
            }
        }
        return toReturn;
    }

    /**
     * @description Checks if the provided cell is outside the viewport
     * @param {*} cellUuid
     * @param {*} cells
     * @param {*} camera
     */
    cellOutsideViewport(cellUuid, cells, camera) {
        const scrolls = camera.scrollbars;
        const cell = cells.get(cellUuid).ref;
        const ratio = camera.transformScaleFactor;
        return cell.layout.x + this.gridConfig.cellWidth < (scrolls.left / ratio) || cell.layout.y + this.gridConfig.cellHeight < (scrolls.top / ratio);
    }

    /**
     * @description gets the cells that need updating after being pierced by a connector
     * @param {Object} initialPoint
     * @param {Object} currentPoint
     * @param {Boolean} intersectionBased
     * @param {GridCell[][]} grid
     */
    getCellsInNeedOfUpdate(initialPoint, currentPoint, intersectionBased, grid, connectingLine) {
        let startX; let endX; let startY; let endY; const
            gridConfig = new GridConfig(this.instanceId);
        const linePoints = { A: initialPoint, B: currentPoint };
        startX = linePoints.A.x > linePoints.B.x ? linePoints.B.x : linePoints.A.x;
        endX = linePoints.A.x > linePoints.B.x ? linePoints.A.x : linePoints.B.x;
        startY = linePoints.A.y > linePoints.B.y ? linePoints.B.y : linePoints.A.y;
        endY = linePoints.A.y > linePoints.B.y ? linePoints.A.y : linePoints.B.y;

        startX -= (startX % gridConfig.cellWidth);
        endX += (gridConfig.cellWidth - (endX % gridConfig.cellWidth));
        startY -= (startY % gridConfig.cellHeight);
        endY += (gridConfig.cellHeight - (endY % gridConfig.cellHeight));

        let startRow = Math.floor((startY - gridConfig.viewPadding) / gridConfig.cellHeight);
        let startCol = Math.floor((startX - gridConfig.viewPadding) / gridConfig.cellWidth);
        let endRow = Math.floor((endY - gridConfig.viewPadding) / gridConfig.cellHeight);
        let endCol = Math.floor((endX - gridConfig.viewPadding) / gridConfig.cellWidth);
        startRow = startRow === 0 ? startRow : startRow - 1;
        endRow = endRow === grid.length ? endRow : endRow + 1;
        startCol = startCol === 0 ? startCol : startCol - 1;
        endCol = endCol === grid[0].length ? endCol : endCol + 1;

        const cells = [];
        for (let row = startRow; row < endRow; row++) {
            for (let cell = startCol; cell < endCol; cell++) {
                const cellRef = grid[row][cell];
                // TODO: Keep using this. Find a way to better keep previously created cells to redraw them.
                // In between mouse movements, the line gets moved but the cells dont redraw for some reason.
                if (!intersectionBased) {
                    // if (cellRef && (cellRef.lineIntersectRectSide([linePoints.A, linePoints.B]) || cellRef.rectCircleColliding(this.connectingLine.arrowHeadPos, cellRef.layout))) {
                    if (grid && grid[row] && grid[row][cell]) {
                        cells.push(grid[row][cell]);
                    }
                    // }
                    continue;
                }
                // let connectingLineColliding = connectingLine && cellRef.rectCircleColliding(connectingLine.arrowHeadPos, cellRef.layout);
                // || connectingLineColliding
                if (cellRef && (cellRef.lineIntersectRectSide([linePoints.A, linePoints.B], true))) {
                    if (grid && grid[row] && grid[row][cell]) {
                        cells.push(grid[row][cell]);
                    }
                }
            }
        }
        return cells;
    }

    /**
     * @description returns the provided array with unique items
     * @param {*} arr
     */
    uniqueArrayItems(arr) {
        return arr.filter((elem, index, array) => array.indexOf(elem) === index);
    }

    /**
     * @description Returns the endpoint that needs to be drawed based on the direction of the mouse relative to the cell
     * @param {*} cell
     * @param {*} dir
     */
    getDraggableEndpointPosition(cell, dir) {
        const container = cell.ref.getContainerInstance();
        const position = { x: null, y: null };
        const { layout } = container;
        if (!dir) {
            position.x = layout.x + (layout.width / 2);
            position.y = layout.y + (layout.height / 2);
            return position;
        }
        if (dir === GRID_DIRECTION.TOP) {
            position.x = layout.x + (layout.width / 2);
            position.y = layout.y;
        }
        if (dir === GRID_DIRECTION.BOTTOM) {
            position.x = layout.x + (layout.width / 2);
            position.y = layout.y + layout.height;
        }
        if (dir === GRID_DIRECTION.LEFT) {
            position.x = layout.x;
            position.y = layout.y + (layout.height / 2);
        }
        if (dir === GRID_DIRECTION.RIGHT) {
            position.x = layout.x + layout.width;
            position.y = layout.y + (layout.height / 2);
        }
        if (dir === GRID_DIRECTION.TOP_LEFT) {
            position.x = layout.x;
            position.y = layout.y;
        }
        if (dir === GRID_DIRECTION.TOP_RIGHT) {
            position.x = layout.x + layout.width;
            position.y = layout.y;
        }
        if (dir === GRID_DIRECTION.BOTTOM_RIGHT) {
            position.x = layout.x + layout.width;
            position.y = layout.y + layout.height;
        }
        if (dir === GRID_DIRECTION.BOTTOM_LEFT) {
            position.x = layout.x;
            position.y = layout.y + layout.height;
        }
        return position;
    }

    getInitialEndpointsCounter() {
        const val = {};
        val[GRID_DIRECTION.LEFT] = val[GRID_DIRECTION.RIGHT] = val[GRID_DIRECTION.BOTTOM] = val[GRID_DIRECTION.TOP] = 0;
        return val;
    }

    /**
     * @description Returns the connectors with the provided ids from the provided map
     * @param {*} ids
     * @param {*} map
     */
    getConnectorsFromMap(ids, map) {
        const value = [];
        for (let i = 0; i < ids.length; i++) {
            value.push(map.get(ids[i]));
        }
        return value;
    }
}
